import React, { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

export default function RecoverEmail() {
  const [form, setForm] = useState({ code: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {matched, masked_email}
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.code.trim()) return setError("Enter the 8-character code on your card");
    if (!/^0\d{9}$/.test(form.phone)) return setError("Enter the 10-digit phone number you used at sign-up");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/recover-email", {
        code: form.code.trim().toUpperCase(),
        phone: form.phone.trim(),
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-md mx-auto px-4 py-12 sm:py-16">
          <Link to="/cardholder/login" className="label-tag inline-flex items-center gap-2" data-testid="recover-back">
            <ArrowLeft size={14} weight="bold" /> Back to sign in
          </Link>
          <div className="mt-4 label-tag flex items-center gap-2"><ShieldCheck size={14} weight="bold" /> Email recovery</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">
            Forgot your email?
          </h1>
          <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
            No stress. Tell us your card code and the phone number you used when activating. We'll send a
            password reset link to the email we have on file.
          </p>

          {!result && (
            <form onSubmit={submit} className="mt-6 space-y-4 bg-white border border-black p-6" data-testid="recover-email-form">
              <div>
                <label className="label-tag">Card code (8 characters)</label>
                <input
                  className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc uppercase tracking-widest"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                  maxLength={8}
                  placeholder="A8EQEZUL"
                  data-testid="recover-code"
                  autoCapitalize="characters"
                  required
                />
                <div className="label-tag text-neutral-500 mt-1">It's the code printed on the front of your card.</div>
              </div>
              <div>
                <label className="label-tag">The phone number you signed up with</label>
                <input
                  className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })}
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="0821234567"
                  data-testid="recover-phone"
                  required
                />
              </div>
              {error && <div className="label-tag text-red-600" data-testid="recover-error">{error}</div>}
              <button type="submit" disabled={submitting} className="w-full bg-[color:var(--cc-yellow)] label-tag px-4 py-4 brutal-btn flex items-center justify-center gap-2 disabled:opacity-60" data-testid="recover-submit">
                <CheckCircle size={16} weight="bold" /> {submitting ? "Checking…" : "Recover my account"}
              </button>
            </form>
          )}

          {result && result.matched && (
            <div className="mt-6 bg-[color:var(--cc-yellow)] border border-black p-6" data-testid="recover-success">
              <div className="label-tag flex items-center gap-2"><CheckCircle size={14} weight="bold" /> Match found</div>
              <div className="font-display font-black text-2xl mt-2 leading-tight">
                Check your inbox.
              </div>
              <p className="mt-3 text-sm leading-relaxed">
                We've emailed a reset link to <span className="font-mono-cc font-semibold">{result.masked_email}</span>.
                Click it within an hour and choose a new password. Then log in with that same email.
              </p>
              <Link to="/cardholder/login" className="mt-4 inline-block label-tag underline">
                Back to sign in
              </Link>
            </div>
          )}

          {result && result.matched === false && (
            <div className="mt-6 bg-white border border-black p-6" data-testid="recover-nomatch">
              <div className="label-tag text-red-600">No match</div>
              <p className="mt-2 text-sm leading-relaxed">
                That card code and phone number don't match what we have on file. Double-check the code on the front of your card
                and the phone number you used at sign-up — then try again. Still stuck? Contact Curb Crew admin and they will help you.
              </p>
              <button onClick={() => { setResult(null); setForm({ code: "", phone: "" }); }} className="mt-4 label-tag underline" data-testid="recover-try-again">
                Try again
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
